import { useEffect } from 'react';
import { EventNames } from '../constants/constants';
import { hasHumanSignal, markHumanSignal, msSinceArrival } from '../../services/analytics/telemetry';
import { useTrackEvent } from './useTrackEvent';

/**
 * Fires `human_signal` once per session at the first thing a real person
 * does. Headless clients and link scanners open the page but never produce a
 * pointer, touch, key or real scroll, so this is the denominator for every
 * "per human" rate. Programmatic scrolls (GA's own <1 s scroll) are excluded
 * by requiring a real delta after the page has been visible for a second.
 */
export const useHumanSignal = (): void => {
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    if (typeof window === 'undefined' || hasHumanSignal()) return undefined;
    const mountedAt = performance.now();
    let done = false;
    let lastScrollY = window.scrollY;

    const fire = (signalType: string, pointerType: string | null) => {
      if (done) return;
      done = true;
      markHumanSignal();
      trackEvent({
        action: EventNames.human_signal,
        signal_type: signalType,
        pointer_type: pointerType,
        t_since_arrival_ms: msSinceArrival(),
      });
      cleanup();
    };
    const onPointer = (e: PointerEvent) => fire('pointer', e.pointerType || null);
    const onTouch = () => fire('touch', 'touch');
    const onKey = () => fire('key', null);
    const onScroll = () => {
      if (performance.now() - mountedAt < 1000) return;
      if (Math.abs(window.scrollY - lastScrollY) < 8) return;
      fire('scroll', null);
    };
    lastScrollY = window.scrollY;
    window.addEventListener('pointerdown', onPointer, { passive: true });
    window.addEventListener('touchstart', onTouch, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    const cleanup = () => {
      window.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('touchstart', onTouch);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
